import * as React from "react";
import { IProductModel } from "../../models";

import "./product.scss";
import { Button } from "../../atoms";
import { useAppDispatch, useAppState, useCopy } from "../../containers";
import classNames from "classnames";
import { ImageGallery } from "..";
import { useCallback, useMemo } from "react";
import { SizeSelection } from "../SizeSelection/size-selection";
import produce from "immer";

interface IProductProps extends IProductContainerProps {
  selected: boolean;
  selectedSize: string;
  copy: {
    addToCart: string;
    removeFromCart: string;
  };
  onButtonClick?: (ev: MouseEvent) => void;
  onSizeSelection?: (size: string) => void;
  showActions?: boolean;
  showSelectedSize?: boolean;
}
const Product: React.FC<IProductProps> = ({
  product: { name, images, sizes },
  copy: { addToCart, removeFromCart },
  selected,
  selectedSize,
  onButtonClick,
  onSizeSelection,
  showSelectedSize = false,
  showActions = true
}) => {
  const baseClassName = "product";

  const className = classNames(baseClassName, {
    "is-active": showActions && selected
  });

  return (
    <div className={className}>
      <div className={`${baseClassName}__image`}>
        <ImageGallery images={images} />
      </div>
      <h3>
        {name}
        {showSelectedSize && selectedSize.length ? ` (${selectedSize})` : ""}
      </h3>

      {showActions && (
        <div className={`${baseClassName}__actions`}>
          {onSizeSelection && (
            <SizeSelection
              sizes={sizes}
              selectedSize={selectedSize}
              onSizeSelection={onSizeSelection}
            />
          )}

          <Button theme={selected ? "dark" : "primary"} onClick={onButtonClick}>
            {selected ? removeFromCart : addToCart}
          </Button>
        </div>
      )}
    </div>
  );
};

interface IProductContainerProps {
  product: IProductModel;
  [key: string]: any;
}

export const ProductContainer: React.FC<IProductContainerProps> = React.memo(
  ({ product, ...props }) => {
    const copy = useCopy("product");
    const dispatch = useAppDispatch();

    const { selectedProduct } = useAppState();

    const handleButtonClick = useCallback(
      (ev: MouseEvent) => {
        ev.preventDefault();

        // @ts-ignore
        if (selectedProduct && selectedProduct.url === product.url) {
          dispatch({
            type: "CLEAR_SELECTION"
          });

          return;
        }

        // @ts-ignore
        if (!selectedProduct || selectedProduct.url !== product.url) {
          dispatch({
            type: "SELECT_PRODUCT",
            value: product
          });
        }
      },
      [selectedProduct, product, dispatch]
    );

    const handleSizeSelection = useCallback(
      size => {
        dispatch({
          type: "SELECT_PRODUCT",
          value: produce(product, draft => {
            draft.selectedSize = size;
          })
        });
      },
      [dispatch, product]
    );

    const [selected, selectedSize = ""] = useMemo(() => {
      if (!selectedProduct) {
        return [false, ""];
      }

      // @ts-ignore
      const isSelected = selectedProduct.url === product.url;
      // @ts-ignore
      return [isSelected, isSelected ? selectedProduct.selectedSize : ""];
    }, [selectedProduct, product]);

    return (
      <Product
        copy={copy}
        selected={selected}
        selectedSize={selectedSize}
        onButtonClick={handleButtonClick}
        onSizeSelection={handleSizeSelection}
        product={product}
        {...props}
      />
    );
  }
);
